import { getMerchantId } from "@/lib/company-storage"
import { fetchCouponWallet, saveWalletCodes, type WalletCoupon } from "@/lib/coupon-wallet"
import { couponCodeKey, getKeptCouponCodes, subscribeCouponStash } from "@/lib/pending-coupon"
import { atom, useAtomValue, useStore } from "jotai"
import { useEffect, useSyncExternalStore } from "react"
import { useAuthStatus } from "./use-auth-status"

type ServerWallet = { status: "unknown" } | { status: "ready"; merchantId: string; coupons: WalletCoupon[] }

/** The signed-in customer's server wallet as last read; server state, never persisted. */
export const serverWalletAtom = atom<ServerWallet>({ status: "unknown" })

const SEPARATOR = "\n"

const offered = new Set<string>()

/** Reads the server wallet again (after a code was saved) and shares it. */
export async function reloadServerWallet(store: ReturnType<typeof useStore>, merchantId: string): Promise<void> {
  const coupons = await fetchCouponWallet(merchantId)
  if (coupons) store.set(serverWalletAtom, { status: "ready", merchantId, coupons })
}

/**
 * The customer's coupons at this store, usable now or later: the server wallet
 * when signed in (assigned plus saved codes; the server drops expired, used
 * and other customers' ones) together with the codes kept on this device.
 * A signed-in customer's device codes the server does not list yet are offered
 * to it once per page load, so the wallet follows them to any device.
 */
export function useCouponWallet(): string[] {
  const store = useStore()
  const authenticated = useAuthStatus()
  const merchantId = getMerchantId()
  const server = useAtomValue(serverWalletAtom)
  const kept = useSyncExternalStore(subscribeCouponStash, () => (merchantId ? getKeptCouponCodes(merchantId).join(SEPARATOR) : ""), () => "")
  const serverKnown = server.status === "ready" && server.merchantId === merchantId

  const serverCodes = serverKnown ? server.coupons.map((coupon) => couponCodeKey(coupon.code)) : []
  const localOnly = (kept ? kept.split(SEPARATOR) : []).filter((code) => !serverCodes.includes(couponCodeKey(code)) && !offered.has(`${merchantId}:${code}`))
  const localOnlyKey = localOnly.join(SEPARATOR)

  useEffect(
    function syncServerWallet() {
      if (!authenticated || !merchantId || (serverKnown && !localOnlyKey)) return
      let cancelled = false
      const toSave = localOnlyKey ? localOnlyKey.split(SEPARATOR) : []
      for (const code of toSave) offered.add(`${merchantId}:${code}`)
      void (toSave.length > 0 ? saveWalletCodes(merchantId, toSave) : Promise.resolve(true)).then(() => {
        if (!cancelled) void reloadServerWallet(store, merchantId)
      })
      return () => {
        cancelled = true
      }
    },
    [authenticated, merchantId, serverKnown, localOnlyKey, store],
  )

  const codes = [...(serverKnown ? server.coupons.map((coupon) => coupon.code) : []), ...(kept ? kept.split(SEPARATOR) : [])]
  const seen = new Set<string>()
  return codes.filter((code) => {
    const key = couponCodeKey(code)
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}
